import React from 'react';
import Button from '../../../shared/components/Button';
import { formatCurrency } from '../../../shared/utils/formatCurrency';

const GroupPreviewScreen = ({ groupData, onNext }) => {
  const frequencyLabel = {
    weekly: 'Weekly',
    'bi-weekly': 'Bi-weekly',
    monthly: 'Monthly'
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-4 mx-auto">
          <span className="text-neutral-50 text-2xl font-bold">E</span>
        </div>
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
          You're Invited!
        </h1>
        <p className="text-neutral-600">
          Join this savings group and start your journey
        </p>
      </div>

      {/* Group Details Card */}
      <div className="max-w-md w-full mx-auto">
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 space-y-4">
          {/* Group Name */}
          <div className="text-center pb-4 border-b border-neutral-100">
            <h2 className="text-xl font-bold text-neutral-900">
              {groupData?.group_name || 'Loading...'}
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              {groupData?.cultural_term || 'Savings'} Group
            </p>
          </div>

          {/* Admin Info */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-neutral-200 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-neutral-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">Your Admin</p>
              <p className="text-xs text-neutral-500">Group Administrator</p>
            </div>
          </div>

          {/* Contribution Details */}
          <div className="space-y-3 pt-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Contribution per Hand</span>
              <span className="text-sm font-semibold text-neutral-900">
                {formatCurrency(groupData?.contribution_amount_per_hand || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Frequency</span>
              <span className="text-sm font-semibold text-neutral-900">
                {frequencyLabel[groupData?.contribution_frequency] || 'Monthly'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Your Position</span>
              <span className="text-sm font-semibold text-neutral-900">TBD</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">First Due Date</span>
              <span className="text-sm font-semibold text-neutral-900">TBD</span>
            </div>
          </div>

          {/* Important Info */}
          <div className="bg-primary-50 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <svg className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-xs text-primary-800">
                <p className="font-medium mb-1">Important Information</p>
                <ul className="space-y-1">
                  <li>• Contributions are due on schedule</li>
                  <li>• All members share responsibility</li>
                  <li>• No income verification required</li>
                  <li>• One hand = one registered member</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-6">
          <Button
            onClick={onNext}
            variant="primary"
            size="lg"
            className="w-full text-lg py-4"
          >
            Join This Group →
          </Button>
        </div>

        {/* Trust Indicators */}
        <div className="mt-6 space-y-2">
          <div className="flex items-center justify-center space-x-2 text-xs text-neutral-500">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>No money held or processed</span>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs text-neutral-500">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Secure and private</span>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs text-neutral-500">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Community-powered</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupPreviewScreen;
